import { Controller } from "@hotwired/stimulus"

// Publishes how much room the timeline column has beyond the reading measure,
// and marks the inline task actions that should spend it.
//
// The activity stream reads on a 42rem measure (`max-w-2xl`), but the column it
// sits in is `grow` with no max-width, so on a wide screen there is unused space
// either side of the text. A task action whose content overflows — a table of
// extracted rows, a comparison — widens into that space rather than scrolling
// inside the measure. When there is no space, and at 1280px there is none
// because the column is narrower than the measure itself, every value here is
// zero and the timeline behaves exactly as it did before.
//
// Measure-in-JS-publish-a-CSS-var follows the same pattern as
// `--composer-clearance` in activity_stream_controller.
export default class extends Controller {
  static targets = ["widen"]
  // How far below a row's top its first line may be, in px, for the time to follow it.
  static FIRST_LINE_REACH = 48
  // Room, in px, a time on a row's last line keeps from the words before it.
  static STAMP_CLEARANCE = 16

  static values = {
    // The reading measure, in px. Keep in step with `max-w-2xl` on the stream.
    reading: { type: Number, default: 672 },
    // A card never grows past this, however wide the display is: past roughly
    // this width a table stops being easier to read and starts being a sprawl.
    cap: { type: Number, default: 1120 },
    // Room kept between a widened card and the column's edge, in px. Without it
    // a card that spends the whole slack lands flush against the sticky page
    // header's rounded corner, and its tape band shows through the notch.
    gutter: { type: Number, default: 16 },
    // Slack a margin time needs, in px: "12:45 PM" at 11px plus its gap, with
    // room to spare (the column's gutter is added to this on screen).
    timeMargin: { type: Number, default: 72 }
  }

  connect() {
    this.column = this.element.parentElement
    this.observer = new ResizeObserver(() => this.schedule())
    if (this.column) this.observer.observe(this.column)
    this.reach = this.reach.bind(this)
    this.leave = this.leave.bind(this)
    this.element.addEventListener("mouseover", this.reach)
    this.element.addEventListener("focusin", this.reach)
    this.element.addEventListener("mouseout", this.leave)
    this.schedule()
  }

  disconnect() {
    this.element.removeEventListener("mouseover", this.reach)
    this.element.removeEventListener("focusin", this.reach)
    this.element.removeEventListener("mouseout", this.leave)
    this.observer?.disconnect()
    if (this.frame) cancelAnimationFrame(this.frame)
    this.element.style.removeProperty("--timeline-slack")
    this.element.removeAttribute("data-time-margin")
  }

  // Cards stream in after the page settles; Stimulus calls this for each one.
  widenTargetConnected() {
    this.schedule()
  }

  // A row's time sits in the left margin level with the row's first line. Rows set their own line heights (a
  // sentence, a note, a card's header), so the line is measured when the pointer or focus reaches the row,
  // which is the only time the time can be seen, and handed to the stamp as a CSS variable.
  reach(event) {
    const article = event.target.closest?.("article")
    if (!article || article === this.reached) return
    this.reached = article
    this.alignStamp(article)
  }

  leave(event) {
    if (this.reached && !this.reached.contains(event.relatedTarget)) this.reached = null
  }

  alignStamp(article) {
    const stamp = article.querySelector(":scope > .event-time-stamp")
    if (!stamp) return
    if (!this.element.hasAttribute("data-time-margin")) return this.alignStampOnLastLine(article, stamp)
    stamp.removeAttribute("data-on-line")
    const center = this.firstLineCenter(article)
    const top = article.getBoundingClientRect().top
    // A first line far down the row is not its first line (a card whose words start below its picture).
    if (center === null || center - top > this.constructor.FIRST_LINE_REACH) stamp.style.removeProperty("--stamp-top")
    else stamp.style.setProperty("--stamp-top", `${Math.round((center - top - stamp.offsetHeight / 2) * 10) / 10}px`)
  }

  // With no margin the time sits at the row's right end, level with its last line (usually the byline), when
  // that line leaves room for it. A last line that runs to the edge keeps the time in the gap below the row.
  alignStampOnLastLine(article, stamp) {
    const line = this.lastLineRect(article)
    const box = article.getBoundingClientRect()
    if (!line || box.right - line.right < stamp.offsetWidth + this.constructor.STAMP_CLEARANCE) {
      stamp.removeAttribute("data-on-line")
      return
    }
    stamp.setAttribute("data-on-line", "")
    stamp.style.setProperty("--stamp-top", `${Math.round((line.top + line.height / 2 - box.top - stamp.offsetHeight / 2) * 10) / 10}px`)
  }

  // The last line of words the row shows: the rect of the last visible text, with the right end of that line.
  lastLineRect(article) {
    const scope = article.querySelector(".event-message-stack") || article
    let node = scope
    while (node.lastChild) node = node.lastChild
    const walker = document.createTreeWalker(scope, NodeFilter.SHOW_TEXT)
    walker.currentNode = node
    let seen = 0
    for (let text = node.nodeType === Node.TEXT_NODE ? node : walker.previousNode(); text && seen < 12; text = walker.previousNode()) {
      if (!text.textContent.trim() || text.parentElement?.closest(".event-time-stamp")) continue
      seen += 1
      const range = document.createRange()
      range.selectNodeContents(text)
      const rects = range.getClientRects()
      const rect = rects[rects.length - 1]
      if (rect && rect.height >= 8) return rect
    }
    return null
  }

  // The vertical middle of the first line of words in the row, or null when it shows none. Text that takes no
  // room (hidden, or read only by a screen reader) is passed over.
  firstLineCenter(article) {
    const scope = article.querySelector(".event-message-stack") || article
    const walker = document.createTreeWalker(scope, NodeFilter.SHOW_TEXT)
    let seen = 0
    for (let node = walker.nextNode(); node && seen < 12; node = walker.nextNode()) {
      if (!node.textContent.trim() || node.parentElement?.closest(".event-time-stamp")) continue
      seen += 1
      const range = document.createRange()
      range.selectNodeContents(node)
      const rect = range.getClientRects()[0]
      if (rect && rect.height >= 8) return rect.top + rect.height / 2
    }
    return null
  }

  schedule() {
    if (this.frame) cancelAnimationFrame(this.frame)
    this.frame = requestAnimationFrame(() => {
      this.frame = null
      this.measure()
    })
  }

  measure() {
    const available = this.column?.clientWidth || 0
    const slack = Math.max(0, Math.round((Math.min(available, this.capValue) - this.readingValue) / 2) - this.gutterValue)
    // The sticky date row reads this too, stretching its mask over whatever a
    // card below it widened into (see .timeline-date-row in markdown.css).
    this.element.style.setProperty("--timeline-slack", `${slack}px`)
    // Every event stamp uses the left margin when it fits; otherwise it sits
    // in the existing gap below the row, outside the content flow.
    this.element.toggleAttribute("data-time-margin", slack >= this.timeMarginValue)

    // Every card is put back on the reading measure before anything is measured,
    // so the question asked is always "does this content need more than the
    // measure?" and never "does it still overflow now that it is wider?" — the
    // second question answers itself differently once the card has widened, and
    // the card would flip state on every resize.
    this.widenTargets.forEach((card) => {
      card.classList.remove("is-wide")
      card.style.removeProperty("--timeline-widen")
      delete card.dataset.densityComfortable
    })
    if (slack <= 0) return

    this.widenTargets.forEach((card) => {
      // A card under a row that clips (a step under an agent's message) would
      // lose both edges past it, so it keeps the row's width and its content
      // fits itself there (a review stacks its rows).
      if (this.clipped(card)) return
      // A card holding content marked wide (a review: rows of values, a
      // decision each) wants every pixel the column can spare, overflowing or
      // not. Any partial can ask for this with `data-timeline-width-wide`.
      const need = card.querySelector("[data-timeline-width-wide]") ? slack : this.roomNeeded(card)
      if (need <= 0) return
      card.style.setProperty("--timeline-widen", `${Math.min(slack, need)}px`)
      card.classList.add("is-wide")
      // Tells table_density_controller to stop compacting this card's tables on
      // their shape alone -- but only once the card has all the room its content
      // needs. A card the column's slack cut short is still squeezing its table,
      // and comfortable padding in a card that is only a little wider breaks
      // "$353.60" across two lines: worse than compact in the narrow card was.
      // The width fallback cannot catch that, because markdown cells wrap
      // (`overflow-wrap: break-word`) rather than overflow, so the table never
      // reports a scrollWidth past its clientWidth. Capped, the card keeps its
      // density and simply gives the table every pixel it gained. It re-fits on
      // its own -- the card's resize is what it watches.
      if (need <= slack) card.dataset.densityComfortable = ""
    })
  }

  // Whether anything between the card and the timeline cuts off what overflows it.
  clipped(card) {
    for (let el = card.parentElement; el && el !== this.element; el = el.parentElement) {
      if (getComputedStyle(el).overflowX !== "visible") return true
    }
    return false
  }

  // How far past the reading measure this card would have to reach, per side,
  // to stop squeezing its widest content; 0 when everything already fits. The
  // caller caps it at the column's slack: taking the whole slack whenever
  // anything overflows leaves a wide card around a table that does not fill it,
  // which reads worse than the narrow card did.
  //
  // Measured against the box the content lays out in -- its parent, inside the
  // card's body padding -- not the card's outer edge, or the padding comes off
  // the answer and the card stops short by exactly that much.
  roomNeeded(card) {
    let need = 0
    card.querySelectorAll("table, pre, [data-timeline-width-measure]").forEach((el) => {
      const short = this.contentWidth(el) - el.parentElement.clientWidth
      if (short > 1) need = Math.max(need, Math.ceil(short / 2))
    })
    return need
  }

  // What the element would take if nothing had been done to make it fit.
  //
  // table_density_controller reaches a wide table before this does: it steps the
  // padding down, then shrinks the type, until the table fits its column. By the
  // time we look, a table that wanted 700px reports 584px and looks content. So
  // measure a copy with the fitting undone. The copy is laid out off-screen and
  // removed in the same frame; `pre` and anything opting in by attribute are
  // simply asked for their scroll width, which nothing has adjusted.
  contentWidth(el) {
    if (el.tagName !== "TABLE") return el.scrollWidth

    const source = el.closest(".markdown-body")
    const host = document.createElement("div")
    host.className = (source?.className || "").replace(/md-content--tight/g, "")
    // Lay the copy out at the type size the real table renders at: the markdown
    // body carries it inline. Without it the copy measures at the stylesheet's
    // size and asks for room the table will never use.
    const fontSize = source?.style.fontSize ? `font-size:${source.style.fontSize};` : ""
    host.style.cssText = `position:absolute;left:-99999px;top:0;width:auto;max-width:none;visibility:hidden;${fontSize}`

    const copy = el.cloneNode(true)
    copy.classList.remove("md-table--compact", "md-table--dense")
    copy.style.cssText = "width:max-content;max-width:none;font-size:inherit;"
    host.appendChild(copy)
    ;(source?.parentElement || this.element).appendChild(host)

    const width = copy.getBoundingClientRect().width
    host.remove()
    return width
  }
}
